import React, { useEffect } from 'react';
import './Modal.css';

/**
 * Modal component — replaces browser alert() dialogs.
 *
 * Props:
 *   isOpen        {bool}     — whether the modal is visible
 *   onClose       {func}     — called when the user dismisses the modal
 *   onConfirm     {func}     — called when the user clicks the primary action (confirm modals only)
 *   title         {string}   — modal title
 *   message       {string}   — main message text
 *   detail        {string}   — optional secondary/detail text
 *   type          {string}   — 'alert' | 'confirm' | 'error' | 'success' | 'warning'
 *   confirmLabel  {string}   — label for confirm button (default: 'Confirmar')
 *   cancelLabel   {string}   — label for cancel button (default: 'Cancelar')
 *   closeLabel    {string}   — label for close button on single-button modals (default: 'Aceptar')
 */
const Modal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  detail,
  type = 'alert',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  closeLabel = 'Aceptar',
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const icons = {
    alert:   { emoji: '⚠️', cls: 'modal-warning' },
    warning: { emoji: '⚠️', cls: 'modal-warning' },
    error:   { emoji: '❌', cls: 'modal-error'   },
    success: { emoji: '✅', cls: 'modal-success'  },
    confirm: { emoji: '❓', cls: 'modal-confirm'  },
  };
  const { emoji, cls } = icons[type] || icons.alert;

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`modal-box ${cls}`} onClick={(e) => e.stopPropagation()}>
        {/* Icon */}
        <div className="modal-icon-wrap">
          <span className="modal-icon">{emoji}</span>
        </div>

        {/* Content */}
        <div className="modal-alert-content">
          {title && <h3 className="modal-title">{title}</h3>}
          {message && <p className="modal-message">{message}</p>}
          {detail && <p className="modal-detail">{detail}</p>}
        </div>

        {/* Actions */}
        <div className="modal-actions">
          {type === 'confirm' ? (
            <>
              <button className="modal-btn modal-btn-cancel" onClick={onClose}>
                {cancelLabel}
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={() => { onConfirm && onConfirm(); onClose(); }}>
                {confirmLabel}
              </button>
            </>
          ) : (
            <button className={`modal-btn modal-btn-close ${type}`} onClick={onClose}>
              {closeLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
